/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from "react-i18next";
import { Box, LinearProgress, makeStyles, Tooltip } from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import { fetchGroup } from "utils/api/groups";
import { Group } from "@dashboard-v3/api";
import { Suspense, useEffect, useState } from "react";
import useSWR from "swr";
import StorageProperties from "../AccountList/StorageProperties";
import { ItemPropertyLabel } from "./Styled";
import apiRequest from "utils/apiRequestWithErrorCode";
import { useSnackbar } from "notistack";
import MemberDetails from "./MemberDetails";
import DotIcon from "@material-ui/icons/FiberManualRecord";
import { GroupId } from "./EmailAndSource";

interface ItemDetailsProps {
  accounts: Group["members"];
  memberGroupsIds: Group["memberGroups"];
  id: string;
}

export default function ItemDetails({
  id,
  accounts,
  memberGroupsIds,
}: ItemDetailsProps) {
  const { t } = useTranslation(["accounts", "rules"]);
  const classes = useStyles();
  const [memberGroups, setMemberGroups] = useState<Group[]>();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingMemberGroups, setLoadingMemberGroups] = useState(false);

  useEffect(() => {
    async function getMemberGroups() {
      setLoadingMemberGroups(true);
      try {
        const res = await apiRequest<Group[]>(
          "GET",
          `/groups/member-groups/${id}`
        );
        setMemberGroups(res);
      } catch (e) {
        enqueueSnackbar(
          "Error while fetching member groups, please try again later",
          { variant: "error" }
        );
      } finally {
        setLoadingMemberGroups(false);
      }
    }

    if (Boolean(memberGroupsIds.length)) {
      getMemberGroups();
    }
  }, []);

  return (
    <>
      <MemberDetails
        label={t("groups.members")}
        members={accounts}
        data-testid="accounts__group-list-item-details"
        renderDetails={account => (
          <div className={classes.dataContainer}>
            <DotIcon className={classes.dotIcon} />
            <div>{account}</div>
          </div>
        )}
      />

      <MemberDetails
        label={t("groups.memberGroups")}
        members={memberGroups || []}
        loading={loadingMemberGroups}
        renderDetails={group => (
          <div className={classes.dataContainer}>
            <DotIcon className={classes.dotIcon} />
            <div>
              <div style={{ fontWeight: 500 }}>{group.name}</div>
              <Tooltip title={group.id} className={classes.groupIdContainer}>
                <div className={classes.groupIdContainer}>
                  <GroupId id={group.id} />
                </div>
              </Tooltip>
            </div>
          </div>
        )}
      />

      <Suspense fallback={<Loading />}>
        <StorageDetails id={id} />
      </Suspense>
    </>
  );
}

function StorageDetails({ id }) {
  const { t } = useTranslation(["accounts"]);

  const { data: groupDetails } = useSWR(`/groups/${id}`, () => fetchGroup(id), {
    suspense: true,
  });

  const storageDetails = groupDetails?.storage;

  if (!storageDetails) return <></>;

  return (
    <>
      <Box mb={3} mt={4}>
        <ItemPropertyLabel>{t("groups.storageAccounts")}</ItemPropertyLabel>
      </Box>
      <StorageProperties
        providers={groupDetails?.storage}
        validateStorage={false}
      />
    </>
  );
}

function Loading() {
  return (
    <Box mb={2} mt={1}>
      <LinearProgress />
    </Box>
  );
}

const useStyles = makeStyles({
  noMembers: { color: grey[500] },
  dataContainer: { display: "flex", alignItems: "center" },
  dotIcon: {
    fontSize: ".5rem",
    margin: "0 10px",
    color: "rgba(0, 0, 0, 0.46)",
  },
  groupIdContainer: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "80%",
  },
  errorText: { fontSize: "11px" },
});
